import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * auto-artifacts: how a run of artifacts renders in a turn, the progress panel and elsewhere.
 *
 *  - A `collection` (gallery, or A/B `comparison` with variants side by side per pair) renders as
 *    one grid with a lightbox: ←/→ navigate, +/− and 0 zoom, Esc closes; captions come from the
 *    filenames. Media inside stays the host's endpoint renderer (decrypted on this device).
 *  - A Claude artifact (claude.ai) renders Viko's sealed snapshot first; the claude.ai page is a
 *    secondary link that names the publishing account, since the browser may be signed in to a
 *    different Claude account.
 *  - An offered deliverable (project policy "ask") is a path card that opens on demand through
 *    the openable-refs resolver; nothing was uploaded.
 */
import { ChevronLeft, ChevronRight, ExternalLink, FileSearch, Minus, Plus, X } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ArtifactPresentation } from './ArtifactPresentation.js';
import { cx, useViewer } from './primitives.js';
import { RefLink } from './RefLink.js';
/** Group a run of artifacts: collections (in collection order), then the rest in order. */
export function partitionArtifacts(items) {
    const out = [];
    const byId = new Map();
    for (const it of items) {
        const c = it.collection;
        if (!c) {
            out.push({ kind: 'single', item: it });
            continue;
        }
        let list = byId.get(c.id);
        if (!list) {
            list = [];
            byId.set(c.id, list);
            out.push({ kind: 'collection', id: c.id, items: list });
        }
        list.push(it);
    }
    for (const [, list] of byId)
        list.sort((a, b) => (a.collection?.index ?? 0) - (b.collection?.index ?? 0));
    return out;
}
/** Comparison rows: one per pair, variants in order. */
export function comparisonRows(items) {
    const rows = new Map();
    for (const it of items) {
        const k = it.collection?.pair ?? it.name;
        rows.set(k, [...(rows.get(k) ?? []), it]);
    }
    return [...rows.entries()].map(([pair, list]) => ({ pair, items: list }));
}
function captionOf(it) {
    return it.caption ?? it.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ');
}
/** A run of consecutive artifacts in a turn (cards), or the progress panel's list (thumbs). */
export function ArtifactRun({ items, variant = 'card', }) {
    const parts = useMemo(() => partitionArtifacts(items), [items]);
    return (_jsx(_Fragment, { children: parts.map((p) => p.kind === 'collection' ? (_jsx(ArtifactCollectionView, { items: p.items }, p.id)) : (_jsx(SingleArtifact, { item: p.item, variant: variant }, p.item.id))) }));
}
export function SingleArtifact({ item, variant = 'card', }) {
    if (item.offered)
        return _jsx(OfferedArtifactCard, { item: item });
    if (item.external)
        return _jsx(ClaudeArtifactCard, { item: item });
    return _jsx(ArtifactPresentation, { artifact: item, variant: variant });
}
export function ArtifactCollectionView({ items }) {
    const first = items[0];
    const c = first?.collection;
    const [open, setOpen] = useState(null);
    const titleId = useId();
    if (!first || !c)
        return null;
    const comparison = c.layout === 'comparison';
    const missing = c.count - items.length;
    const tile = (it) => {
        const index = items.indexOf(it);
        return (_jsxs("figure", { className: "vk-coll__cell", children: [_jsx("button", { type: "button", className: "vk-coll__open", onClick: () => setOpen(index), "aria-label": `Open ${captionOf(it)} (${index + 1} of ${items.length})`, children: _jsx(ArtifactPresentation, { artifact: it, variant: "thumb" }) }), _jsxs("figcaption", { className: "vk-coll__cap", children: [comparison && it.collection?.variant ? (_jsx("span", { className: "vk-coll__variant", children: it.collection.variant })) : null, _jsx("span", { className: "vk-trunc", children: comparison ? (it.collection?.pair ?? captionOf(it)) : captionOf(it) })] })] }, it.id));
    };
    return (_jsxs("section", { className: cx('vk-coll', `vk-coll--${c.layout}`), "aria-labelledby": titleId, children: [_jsxs("header", { className: "vk-coll__head", children: [_jsx("span", { className: "vk-coll__kind", children: comparison ? 'Comparison' : 'Gallery' }), _jsx("span", { id: titleId, className: "vk-coll__title", children: c.title ?? (comparison ? 'Comparison' : 'Images') }), _jsxs("span", { className: "vk-coll__count", children: [items.length.toLocaleString('en-US'), missing > 0 ? ` of ${c.count.toLocaleString('en-US')}` : '', ' ', c.count === 1 ? 'image' : 'images'] })] }), comparison ? (_jsx("div", { className: "vk-coll__rows", children: comparisonRows(items).map((row) => (_jsx("div", { className: "vk-coll__row", style: { ['--vk-cols']: String(Math.max(2, row.items.length)) }, children: row.items.map(tile) }, row.pair))) })) : (_jsx("div", { className: "vk-coll__grid", children: items.map(tile) })), open !== null ? (_jsx(Lightbox, { items: items, index: open, onIndex: setOpen, onClose: () => setOpen(null) })) : null] }));
}
const ZOOMS = [1, 1.5, 2, 3, 4];
/** Full-screen viewer: keyboard nav, zoom, swipe, captions. Not a bottom sheet. */
export function Lightbox({ items, index, onIndex, onClose, }) {
    const [zoom, setZoom] = useState(0);
    const dialog = useRef(null);
    const touch = useRef(null);
    const it = items[index];
    const go = useCallback((d) => {
        setZoom(0);
        onIndex((index + d + items.length) % items.length);
    }, [index, items.length, onIndex]);
    useEffect(() => {
        const prev = document.activeElement;
        dialog.current?.focus();
        return () => prev?.focus?.();
    }, []);
    const onKey = (e) => {
        if (e.key === 'Escape')
            onClose();
        else if (e.key === 'ArrowRight')
            go(1);
        else if (e.key === 'ArrowLeft')
            go(-1);
        else if (e.key === '+' || e.key === '=')
            setZoom((z) => Math.min(ZOOMS.length - 1, z + 1));
        else if (e.key === '-' || e.key === '_')
            setZoom((z) => Math.max(0, z - 1));
        else if (e.key === '0')
            setZoom(0);
        else
            return;
        e.preventDefault();
    };
    if (!it)
        return null;
    const scale = ZOOMS[zoom] ?? 1;
    const variant = it.collection?.layout === 'comparison' ? it.collection.variant : undefined;
    return (_jsxs("div", { ref: dialog, className: "vk-lightbox", role: "dialog", "aria-modal": "true", "aria-label": `${captionOf(it)}, ${index + 1} of ${items.length}`, tabIndex: -1, onKeyDown: onKey, onTouchStart: (e) => {
            touch.current = e.touches[0]?.clientX ?? null;
        }, onTouchEnd: (e) => {
            const start = touch.current;
            const end = e.changedTouches[0]?.clientX;
            touch.current = null;
            if (start == null || end == null || scale > 1)
                return;
            if (end - start > 60)
                go(-1);
            else if (start - end > 60)
                go(1);
        }, children: [_jsxs("div", { className: "vk-lightbox__bar", children: [_jsxs("span", { className: "vk-lightbox__pos", children: [(index + 1).toLocaleString('en-US'), " / ", items.length.toLocaleString('en-US')] }), _jsxs("span", { className: "vk-lightbox__cap", children: [variant ? _jsx("span", { className: "vk-coll__variant", children: variant }) : null, captionOf(it)] }), _jsx("button", { type: "button", className: "vk-lightbox__btn", onClick: () => setZoom((z) => Math.max(0, z - 1)), disabled: zoom === 0, "aria-label": "Zoom out", children: _jsx(Minus, { size: 16 }) }), _jsxs("button", { type: "button", className: "vk-lightbox__btn vk-lightbox__zoom", onClick: () => setZoom(0), "aria-label": `Zoom ${Math.round(scale * 100)} percent; reset`, children: [Math.round(scale * 100), "%"] }), _jsx("button", { type: "button", className: "vk-lightbox__btn", onClick: () => setZoom((z) => Math.min(ZOOMS.length - 1, z + 1)), disabled: zoom === ZOOMS.length - 1, "aria-label": "Zoom in", children: _jsx(Plus, { size: 16 }) }), _jsx("button", { type: "button", className: "vk-lightbox__btn", onClick: onClose, "aria-label": "Close", children: _jsx(X, { size: 18 }) })] }), _jsxs("div", { className: "vk-lightbox__stage", children: [_jsx("button", { type: "button", className: "vk-lightbox__nav vk-lightbox__nav--prev", onClick: () => go(-1), "aria-label": "Previous", disabled: items.length < 2, children: _jsx(ChevronLeft, { size: 22 }) }), _jsx("div", { className: cx('vk-lightbox__media', scale > 1 && 'is-zoomed'), children: _jsx("div", { className: "vk-lightbox__scaled", style: { width: `${scale * 100}%` }, children: _jsx(ArtifactPresentation, { artifact: it, variant: "thumb" }, it.id) }) }), _jsx("button", { type: "button", className: "vk-lightbox__nav vk-lightbox__nav--next", onClick: () => go(1), "aria-label": "Next", disabled: items.length < 2, children: _jsx(ChevronRight, { size: 22 }) })] }), it.path ? _jsx("code", { className: "vk-lightbox__path vk-mono", children: it.path }) : null] }));
}
/** A claude.ai artifact: Viko's sealed snapshot first, the claude.ai page second. */
export function ClaudeArtifactCard({ item, preview }) {
    const ext = item.external;
    const { refs } = useViewer();
    if (!ext)
        return null;
    const by = ext.accountLabel ?? ext.accountId;
    const title = ext.title ?? item.name;
    return (_jsxs("section", { className: "vk-claudeart", "aria-label": `Claude artifact ${title}`, children: [_jsxs("header", { className: "vk-claudeart__head", children: [_jsx("span", { className: "vk-coll__kind", children: "Claude artifact" }), _jsx("span", { className: "vk-claudeart__title", children: title }), ext.version ? (_jsxs("span", { className: "vk-claudeart__ver", children: ["v", ext.version.toLocaleString('en-US')] })) : null] }), ext.description ? _jsx("p", { className: "vk-claudeart__desc", children: ext.description }) : null, ext.snapshot ? ((preview ?? (_jsx(ArtifactPresentation, { artifact: { ...item, external: undefined }, variant: "card" })))) : (_jsxs("p", { className: "vk-claudeart__note", role: "status", children: ["No snapshot in Viko", ext.snapshotMissing ? ` (${ext.snapshotMissing})` : '', ". It opens on claude.ai only."] })), _jsxs("div", { className: "vk-claudeart__links", children: [ext.url ? (_jsxs("a", { className: "vk-claudeart__link", href: ext.url, target: "_blank", rel: "noopener noreferrer", children: [_jsx(ExternalLink, { size: 14, "aria-hidden": "true" }), "Open on claude.ai", by ? ` as ${by}` : ''] })) : null, ext.liveUrl && refs ? (_jsx(RefLink, { refValue: { kind: 'url', text: ext.liveUrl, strength: 'strong' }, children: "Live preview" })) : null] }), ext.url && by ? (_jsxs("p", { className: "vk-claudeart__hint", children: ["Published by ", by, ". claude.ai shows it only when this browser is signed in to that Claude account; switch accounts on claude.ai if it says the page is not found."] })) : null] }));
}
/** A deliverable Viko found but did not upload (policy "ask"): open it, or publish it. */
export function OfferedArtifactCard({ item }) {
    const path = item.path ?? item.name;
    const { publishArtifact } = useViewer();
    const [state, setState] = useState('idle');
    const [error, setError] = useState();
    const publish = async () => {
        if (!publishArtifact)
            return;
        setState('busy');
        try {
            await publishArtifact(path);
            setState('done');
        }
        catch (e) {
            setError(e instanceof Error ? e.message : 'Could not publish');
            setState('error');
        }
    };
    // agent-sharing: a file in a folder outside the project; publishing it allows that folder.
    const root = item.shareRoot;
    const label = state === 'done'
        ? root
            ? `Shared — Viko can now show files from ${root}`
            : 'Published — it appears in this turn'
        : root
            ? `Share ${root} with Viko?`
            : 'Found, not published';
    return (_jsxs("div", { className: "vk-offered", role: "group", "aria-label": root ? `Share ${root} with Viko: ${path}` : `Not published: ${path}`, children: [_jsx(FileSearch, { size: 16, "aria-hidden": "true" }), _jsxs("span", { className: "vk-offered__main", children: [_jsx("span", { className: "vk-offered__label", children: label }), _jsx(RefLink, { refValue: { kind: 'path', text: path, strength: 'strong' }, fallback: _jsx("code", { className: "vk-mono", children: path }), children: _jsx("code", { className: "vk-mono", children: path }) }), state === 'error' ? (_jsx("span", { className: "vk-offered__error", role: "alert", children: error })) : null] }), publishArtifact && state !== 'done' ? (_jsx("button", { type: "button", className: "vk-offered__publish", onClick: () => void publish(), disabled: state === 'busy', "aria-label": root ? `Allow ${root} and publish ${path}` : `Publish ${path} to Viko`, children: state === 'busy' ? 'Publishing…' : root ? 'Share' : 'Publish' })) : null] }));
}
//# sourceMappingURL=ArtifactCollection.js.map